import React from 'react';
import {Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import ChartBlock from './ChartBlock';
import ChartTooltip from './ChartTooltip';
import useChartPalette from './palette';
import {formatCount} from './format';
import './charts.css';

/*
 * Bars, in one of two directions, stacked or not.
 *
 * `layout` is recharts' own word and reads backwards: "horizontal" lays the
 * categories along the bottom and grows the bars upward, "vertical" puts the
 * categories down the side and grows them to the right. The second is for
 * categories with names - a bot, an operator - because a name written under a
 * bar has to be turned on its side to fit, and a column of names does not.
 *
 * One entry in `series` draws plain bars, and each row may then carry its own
 * `color`; several entries stack them in the order given.
 *
 * `data` rows are {name, <series key>: number, ...}.
 */
/* recharts gives a click handler the shape's props, where the row it was drawn
   from sits on `payload` - and in some charts is also spread alongside it. The
   caller wants the row either way. */
const rowOf = (clicked) => (clicked && clicked.payload) ? clicked.payload : clicked;

const BarsChart = ({
                       titleKey,
                       hintKey,
                       data = [],
                       series = [],
                       layout = 'horizontal',
                       height,
                       categoryWidth = 120,
                       onBarClick,
                       /* for an axis that counts something other than things -
                          seconds, where "3600" means an hour */
                       valueFormatter,
                   }) => {

    const palette = useChartPalette();
    const isVertical = layout === 'vertical';

    const hasValues = data.some(row =>
        series.some(item => (row[item.key] || 0) > 0)
    );

    const axisStyle = {fill: palette.axis, fontSize: 11};

    const categoryAxis = isVertical
        ? <YAxis
            type="category"
            dataKey="name"
            width={categoryWidth}
            tick={axisStyle}
            axisLine={false}
            tickLine={false}
        />
        : <XAxis
            type="category"
            dataKey="name"
            tick={axisStyle}
            axisLine={{stroke: palette.grid}}
            tickLine={false}
        />;

    const tickFormatter = valueFormatter || formatCount;

    const valueAxis = isVertical
        ? <XAxis type="number" tick={axisStyle} axisLine={false} tickLine={false} tickFormatter={tickFormatter}/>
        : <YAxis type="number" tick={axisStyle} axisLine={false} tickLine={false} tickFormatter={tickFormatter}/>;

    return (
        <ChartBlock titleKey={titleKey} hintKey={hintKey} isEmpty={!hasValues} height={height}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={data}
                    layout={layout}
                    margin={{top: 8, right: 12, bottom: 0, left: isVertical ? 0 : -18}}
                    barCategoryGap={isVertical ? '28%' : '22%'}
                >
                    <CartesianGrid
                        stroke={palette.grid}
                        /* only across the value axis: a line behind every
                           category name is noise, not a reading aid */
                        vertical={isVertical}
                        horizontal={!isVertical}
                    />
                    {categoryAxis}
                    {valueAxis}
                    <Tooltip
                        content={<ChartTooltip valueFormatter={valueFormatter}/>}
                        cursor={{fill: palette.grid, fillOpacity: 0.35}}
                    />

                    {series.map((item, index) => (
                        <Bar
                            key={item.key}
                            dataKey={item.key}
                            name={item.name}
                            fill={item.color || palette.series1}
                            stackId={series.length > 1 ? 'stack' : undefined}
                            radius={series.length > 1
                                ? 0
                                : (isVertical ? [0, 4, 4, 0] : [4, 4, 0, 0])}
                            onClick={onBarClick ? (clicked => onBarClick(rowOf(clicked))) : undefined}
                            cursor={onBarClick ? 'pointer' : 'default'}
                        >
                            {/* per-row colours only make sense without a stack,
                                where a row is one bar rather than one segment */}
                            {series.length === 1 && index === 0 && data.map((row, rowIndex) => (
                                <Cell key={rowIndex} fill={row.color || item.color || palette.series1}/>
                            ))}
                        </Bar>
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </ChartBlock>
    );
};

export default BarsChart;



// WEBPACK FOOTER //
// ./src/components-new/general/charts/BarsChart.jsx